import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";

// Components
import TextInput from "new_components/molecules/TextInput";
import ToggleItem from "new_components/molecules/ToggleItem";
import ImageButton from "new_components/molecules/ImageButton";
import SelectBox from "new_components/molecules/SelectBox";
import Text from "new_components/atoms/Text"; // Import the Text component
import Tooltip from "new_components/molecules/Tooltip";

// Constants
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import colors from "styles/colors";
import typography from "styles/typography";

const allowQuantitative = true;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const QuestionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: ${colors.background.lightBlue};
  padding: 16px;
  border-radius: 8px;
`;

const OptionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  display: ${({ noEdit }) => (noEdit ? 'none' : 'flex')};
`;

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  margin-top: 20px;
`;

const QualifierInputWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const Qualifier = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 25%;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const OptionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TypeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
`;

const Questions = ({ questions, editable, onUpdate, errors, noEdit = false }) => {
  const handleQuestionChange = (index, field, value) => {
    if (!editable) return;
    const updatedQuestions = [...questions];
    // If the field is type, and its value is quantifier default the first option to "0-10"
    if (field === "type" && value === "scale") {
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        [field]: value,
        scale: "0-10",
      };
    } else if (field === "type" && (value === "multi" || value === "single")) {
      // Preserve existing options if they exist, otherwise initialize with one empty option
      const existingOptions = updatedQuestions[index].options || [""];
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        [field]: value,
        options: existingOptions,
      };
    } else {
      updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    }
    onUpdate(updatedQuestions);
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    if (!editable) return;
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    const options = [...(question.options || [""])];
    options[optionIndex] = value;
    updatedQuestions[questionIndex].options = options;
    onUpdate(updatedQuestions);
  };

  const handleAddOption = (questionIndex) => {
    if (!editable) return;
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    const options = [...(question.options || []), ""];
    updatedQuestions[questionIndex].options = options;
    onUpdate(updatedQuestions);
  };

  const handleRemoveOption = (questionIndex, optionIndex) => {
    if (!editable) return;
    const updatedQuestions = [...questions];
    const question = updatedQuestions[questionIndex];
    const options = question.options.filter((_, index) => index !== optionIndex);
    updatedQuestions[questionIndex].options = options;
    onUpdate(updatedQuestions);
  };

  const handleQualifierChange = (index, position, value) => {
    if (!editable) return;
    const updatedQuestions = [...questions];
    const question = updatedQuestions[index];

    // If scaleOptions is present, don't allow changes
    if (question.scaleOptions) {
      return;
    }

    const currentQualifiers = question.qualifier || [
      "Strongly Disagree",
      "Neutral",
      "Strongly Agree",
    ];
    const newQualifiers = [...currentQualifiers];
    // position: 0 for minimum, 1 for middle, 2 for maximum
    newQualifiers[position] = value;
    updatedQuestions[index].qualifier = newQualifiers;
    onUpdate(updatedQuestions);
  };

  const handleRemoveQuestion = (index) => {
    if (!editable) return;
    const updatedQuestions = questions.filter((_, i) => i !== index);
    onUpdate(updatedQuestions);
  };

  const handleAddQuestion = () => {
    if (!editable) return;
    const updatedQuestions = [
      ...questions,
      {
        id: uuidv4(),
        question: "",
        aiEnhanced: true, // default to true
        task_type: "question",
        type: "text", // default to text
        scale: null,
        qualifier: null,
        options: null,
      },
    ];
    onUpdate(updatedQuestions);
  };

  return (
    <SectionWrapper>
      {questions.map((question, index) => (
        <QuestionWrapper key={question.id || index}>
          {/* Question Text */}
          <TextInput
            label={`Question #${index + 1}`}
            value={question.question}
            onChange={(e) => handleQuestionChange(index, "question", e)}
            placeholder="Enter your question"
            readOnly={!editable}
            error={errors?.surveyQuestionValidation && question.question === ""}
          />

          <TypeRow>
            {allowQuantitative && (
              <FieldRow>
                <SelectBox
                  width={"220px"}
                  label="Question Type"
                  readOnly={!editable}
                  value={question.type}
                  onSelect={(value) =>
                    handleQuestionChange(index, "type", value)
                  }
                  disabled={!editable}
                  options={[
                    { value: "text", label: "Open Text", icon: ICON_SET.pulseText },
                    { value: "scale", label: "Sliding Scale", icon: ICON_SET.pulseScale },
                    { value: "multi", label: "Multiple Answer", icon: ICON_SET.pulseMulti },
                    { value: "single", label: "Multiple Choice", icon: ICON_SET.pulseSingle },
                  ]}
                  placeholder="Select type"
                />
              </FieldRow>
            )}

            {question.type === "scale" && (
              <FieldRow>
                <SelectBox
                  label="Scale"
                  readOnly={!editable}
                  width={"200px"}
                  options={[
                    { value: "0-10", label: "10 Point" },
                    { value: "0-5", label: "5 Point" },
                  ].map((option) => {
                    return {
                      value: option.value,
                      label: option.label,
                    };
                  })}
                  placeholder="Select scale"
                  value={question.scale}
                  onSelect={(value) =>
                    handleQuestionChange(index, "scale", value)
                  }
                  disabled={!editable}
                />
              </FieldRow>
            )}
          </TypeRow>

          {question.type === "scale" && (
            <FieldRow>
              <Text
                typographyStyle={typography.caption.semibold}
                color={colors.text.dark}
              >
                Qualifiers:
              </Text>
              <QualifierInputWrapper>
                <Qualifier>
                  <TextInput
                    placeholder="Strongly Disagree"
                    readOnly={!editable}
                    value={
                      question.scaleOptions
                        ? question.scaleOptions[0]
                        : question.qualifier
                        ? question.qualifier[0]
                        : "Strongly Disagree"
                    }
                    onChange={(e) => handleQualifierChange(index, 0, e)}
                    label="Minimum"
                    disabled={!editable || question.scaleOptions}
                  />
                </Qualifier>
                <Qualifier>
                  <TextInput
                    placeholder="Neutral"
                    readOnly={!editable}
                    value={
                      question.scaleOptions
                        ? question.scaleOptions[Math.floor(question.scaleOptions.length / 2)]
                        : question.qualifier
                          ? question.qualifier[1]
                          : "Neutral"
                    }
                    onChange={(e) => handleQualifierChange(index, 1, e)}
                    label="Middle"
                    disabled={!editable || question.scaleOptions}
                  />
                </Qualifier>
                <Qualifier>
                  <TextInput
                    placeholder="Strongly Agree"
                    readOnly={!editable}
                    value={
                      question.scaleOptions
                        ? question.scaleOptions[question.scaleOptions.length - 1]
                        : question.qualifier
                        ? question.qualifier[2]
                        : "Strongly Agree"
                    }
                    onChange={(e) => handleQualifierChange(index, 2, e)}
                    label="End"
                    disabled={!editable || question.scaleOptions}
                  />
                </Qualifier>
              </QualifierInputWrapper>
            </FieldRow>
          )}

          {(question.type === "multi" || question.type === "single") && (
            <OptionsContainer>
              <Text
                typographyStyle={typography.caption.semibold}
                color={colors.text.dark}
              >
                Options:
              </Text>
              {(question.options || [""]).map((option, optionIndex) => (
                <OptionRow key={optionIndex}>
                  <TextInput
                    value={option}
                    defaultValue={`Option ${optionIndex + 1}`}
                    onChange={(e) => handleOptionChange(index, optionIndex, e)}
                    readOnly={!editable}
                    width="100%"
                  />
                  {editable && question.options?.length > 1 && (
                    <ImageButton
                      icon={ICON_SET.delete}
                      textStyle={true}
                      color={colors.auxiliary.red[500]}
                      onClick={() => handleRemoveOption(index, optionIndex)}
                    />
                  )}
                </OptionRow>
              ))}
              {editable && (
                <ImageButton
                  text="Add option"
                  icon={ICON_SET.add}
                  iconPosition="left"
                  textStyle={true}
                  onClick={() => handleAddOption(index)}
                  color={colors.primary[500]}
                />
              )}
            </OptionsContainer>
          )}

          <OptionsRow noEdit={!editable}>
            {/* AI Enhanced Toggle */}
            <Tooltip tooltipText="Use an AI-designed follow-up question to encourage the participant to elaborate on their response.">
              <ToggleItem
                isActive={question.aiEnhanced}
                onToggle={(value) =>
                  handleQuestionChange(index, "aiEnhanced", value)
                }
                title="AI Enhanced"
                disabled={!editable}
              />
            </Tooltip>
            {editable && (
              <ImageButton
                text="Remove question"
                icon={ICON_SET.delete}
                iconPosition="left"
                textStyle={true}
                color={colors.auxiliary.red[500]}
                onClick={() => handleRemoveQuestion(index)}
              />
            )}
          </OptionsRow>
        </QuestionWrapper>
      ))}

      {editable && (
        <ImageButton
          text="Add question"
          icon={ICON_SET.add}
          iconPosition="left"
          textStyle={true}
          onClick={handleAddQuestion}
          color={colors.primary[500]}
        />
      )}
    </SectionWrapper>
  );
};

Questions.propTypes = {
  questions: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string.isRequired,
      aiEnhanced: PropTypes.bool.isRequired,
      task_type: PropTypes.string.isRequired,
      type: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        }),
      ]),
      scale: PropTypes.oneOf(["0-10", "1-5", "0-5", null]),
      qualifier: PropTypes.arrayOf(PropTypes.string),
      scaleOptions: PropTypes.arrayOf(PropTypes.string),
      options: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  editable: PropTypes.bool,
  onUpdate: PropTypes.func,
};

Questions.defaultProps = {
  editable: true, // Default to editable
  onUpdate: () => {}, // Default no-op function
};

export default Questions;
